/* =========================================================================
   Mega Nav — interaction layer (no dependencies)
   - Attaches "Mega Nav · Dropdown" (mega-nav-item) sections (panel, label, mobile content) to the bar
   - Hover intent with open/close delays, click and touch handling
   - Full keyboard support (arrows, Escape, focus management)
   - Tabs layout switching, accordions in the drawer
   - Mobile drawer with drill-down panels and focus trap
   - Optional mounting into the theme's own header
   - Theme Editor integration (section/block select opens the matching panel)
   Public: window.MegaNav (class), MegaNav.instances, MegaNav.attach(el), MegaNav.detach(sectionId),
           MegaNav.THEMES (header presets; keep in step with the name guess in sections/mega-nav.liquid), MegaNav.version
   Events: meganav:ready, meganav:open, meganav:close, meganav:drawer:open, meganav:drawer:close, meganav:drawer:panel,
           meganav:click (see docs/tracking.md)
   Reading order follows init(): resolvePlacement → setupMode → mountBar → hideThemeElements → attach → bind*
   ========================================================================= */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href]:not([tabindex="-1"]):not([aria-hidden="true"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var isVisible = function (n) { var cs = window.getComputedStyle(n); return cs.visibility !== 'hidden' && cs.display !== 'none' && (n.offsetWidth > 0 || n.offsetHeight > 0 || n.getClientRects().length > 0); };
  var slice = function (list) { return Array.prototype.slice.call(list || []); };
  var esc = function (v) { return window.CSS && CSS.escape ? CSS.escape(String(v)) : String(v).replace(/["\\]/g, '\\$&'); };

  function MegaNav(root) {
    var t0 = window.performance ? performance.now() : 0;
    this.root = root;
    root.setAttribute('data-mn-ready', '');
    try { this.settings = JSON.parse(root.getAttribute('data-mn-settings') || '{}'); } catch (e) { this.settings = {}; }
    var s = this.settings;
    s.hoverOpenDelay = Number(s.hoverOpenDelay) || 0;
    s.hoverCloseDelay = Number(s.hoverCloseDelay) || 0;
    s.breakpoint = Number(s.breakpoint) || 990;
    if (s.openOn !== 'click') s.openOn = 'hover';

    this.bar = root.querySelector('[data-mn-bar]');
    this.drawer = root.querySelector('[data-mn-drawer]');
    this.overlay = root.querySelector('[data-mn-overlay]');
    this.drawerOverlay = root.querySelector('[data-mn-drawer-overlay]');
    this.openItem = null;
    this.timers = { open: null, close: null, tab: null, drawer: null, switching: null };
    this.lastPointerType = 'mouse';
    this.drawerStack = ['root'];
    this.drawerOpen = false;
    this.mounted = false;
    this.editorHold = false;
    this.mode = 'desktop';
    this.listeners = [];
    this.sections = {};

    MegaNav.instances.push(this);
    this.init();
    this.initMs = window.performance ? performance.now() - t0 : 0;
  }

  MegaNav.instances = [];
  MegaNav.version = '1.2.1';

  /* Known theme headers: where our bar goes, and which theme button should open our drawer.
     'auto' tries these in order and falls back to a plain row when none matches.
     Horizon is a sibling mount ('after'): its header-menu component observes its own children and throws
     when they change, so we never render inside it. sections/mega-nav.liquid carries the matching
     server-side name guess and early-hide selectors: change both together. */
  MegaNav.THEMES = {
    /* alts: tried in order when the first target is not on the page. Horizon only renders <header-menu>
       when the header carries a menu block, so fall back to a row of our own inside the header component. */
    /* Horizon draws its menu in one of two places: the inline <header-menu>, or a "navigation bar"
       row underneath the logo, which is a plain div carrying data-navigation-bar-enabled. Both are
       hidden, and the row is a mount target in its own right, because a header set to the drawer
       menu style renders the inline menu hidden and shows only the row. */
    horizon: { label: 'Horizon', mount: 'header-menu', mode: 'after', trigger: 'header-drawer summary',
      hide: 'header-menu, [data-navigation-bar-enabled]',
      alts: [{ mount: '#header-component .header__navigation-bar-row', mode: 'append' },
             { mount: '#header-component', mode: 'append' }] },
    dawn:    { label: 'Dawn', mount: '.header__inline-menu', mode: 'replace', trigger: 'header-drawer summary', hide: '' },
    debut:   { label: 'Debut', mount: '#AccessibleNav', mode: 'replace', trigger: '.js-mobile-nav-toggle', hide: '#MobileNav' },
    mamas:   { label: 'Mamas & Papas', mount: '#MasterNav', mode: 'replace', trigger: '.js-mobile-nav-toggle', hide: '.mobile-nav-wrapper' }
  };
  MegaNav.THEME_ORDER = ['mamas', 'horizon', 'dawn', 'debut'];

  /* ------------------------------------------------------------------ */
  /* Attaching item sections                                             */
  /* ------------------------------------------------------------------ */

  MegaNav.attach = function (el) {
    var inst = MegaNav.instances[0];
    if (!el || !inst) return false;
    return inst.attachSection(el);
  };

  MegaNav.detach = function (sectionId) {
    var inst = MegaNav.instances[0];
    if (inst) inst.detachSection(sectionId);
  };

  MegaNav.prototype.attachAll = function () {
    var self = this;
    slice(document.querySelectorAll('[data-mn-item-section]:not([data-mn-attached])')).forEach(function (el) {
      self.attachSection(el);
    });
    this.hoistSubpanels();
  };

  MegaNav.prototype.attachSection = function (el) {
    if (!el || el.getAttribute('data-mn-attached') === 'true' || !this.bar) return false;
    var key = el.getAttribute('data-mn-for');
    var sid = el.getAttribute('data-section-id');
    var idx = el.getAttribute('data-mn-index');
    var li = this.bar.querySelector('[data-mn-item][data-mn-key="' + esc(key) + '"]');
    if (!li) return false;

    this.detachSection(sid);
    var self = this;
    Object.keys(this.sections).forEach(function (other) {
      if (self.sections[other].key === key) {
        if (window.console && (self.settings.designMode || (window.Shopify && window.Shopify.designMode))) console.warn('Mega Nav: two Dropdown sections target "' + key + '"; the newest one is used.');
        self.duplicateKeys = self.duplicateKeys || {}; self.duplicateKeys[key] = true;
        self.detachSection(other);
      }
    });
    this.sections[sid] = { key: key, index: idx };
    // A Dropdown section carries its own menu setting, so it can end up reading a different menu
    // from the bar. The item then matches but has no groups, which looks like a Navigation problem.
    var itemMenu = el.getAttribute('data-mn-menu');
    if (itemMenu && this.settings.menu && itemMenu !== this.settings.menu) {
      this.menuMismatch = this.menuMismatch || {};
      this.menuMismatch[itemMenu] = true;
      if (window.console && (this.settings.designMode || (window.Shopify && window.Shopify.designMode))) {
        console.warn('Mega Nav: a Dropdown section is reading the menu "' + itemMenu + '" but the bar uses "' + this.settings.menu + '". Set both to the same menu.');
      }
    }
    if (this.settings.designMode) this.noteSections();

    // Top-level label
    var link = li.querySelector('[data-mn-link]');
    var label = el.querySelector('[data-mn-bar-label]');
    if (link && label) {
      if (!link.hasAttribute('data-mn-original')) link.setAttribute('data-mn-original', link.innerHTML);
      link.innerHTML = label.innerHTML;
      link.setAttribute('data-mn-labelled', sid);
    }
    var style = el.getAttribute('data-mn-item-style');
    if (style) {
      style.split(';').forEach(function (decl) { var i = decl.indexOf(':'); if (i > 0) li.style.setProperty(decl.slice(0, i).trim(), decl.slice(i + 1).trim()); });
      li.setAttribute('data-mn-styled', sid);
    }
    li.classList.toggle('mn__item--highlight', el.hasAttribute('data-mn-highlight'));

    // Desktop panel
    var panel = el.querySelector('[data-mn-panel]');
    var oldPanel = li.querySelector(':scope > [data-mn-panel]:not([data-mn-from])');
    if (oldPanel && panel) { oldPanel.hidden = true; oldPanel.setAttribute('data-mn-replaced', sid); }
    if (panel) {
      panel.setAttribute('data-mn-from', sid);
      li.appendChild(panel);
      li.classList.add('mn__item--has-panel');
      li.setAttribute('data-mn-layout', el.getAttribute('data-mn-layout') || 'columns');
      li.setAttribute('data-mn-width', el.getAttribute('data-mn-width') || 'full');
      li.classList.toggle('mn__item--anchored', li.getAttribute('data-mn-width') === 'anchored');
      li.classList.toggle('mn__item--full', li.getAttribute('data-mn-width') !== 'anchored');
      var tg = li.querySelector(':scope > [data-mn-toggle]');
      if (tg) { tg.setAttribute('aria-controls', panel.id); tg.setAttribute('aria-expanded', 'false'); }
    } else if (!oldPanel) {
      // Nothing to show from either side, so the item is a plain link
      li.classList.remove('mn__item--has-panel', 'mn__item--anchored', 'mn__item--full');
      li.removeAttribute('data-mn-layout');
      var lk = li.querySelector(':scope > [data-mn-link]');
      if (lk) { lk.removeAttribute('aria-expanded'); lk.removeAttribute('aria-controls'); }
    }
    // else: this section has no dropdown of its own (usually a Dropdown section pointed at the wrong
    // menu, so it sees no groups). Leave the automatic dropdown in place rather than taking it away:
    // a half-filled section must never make a working item stop opening.

    // Drawer content
    if (this.drawer && idx) {
      var dpanel = this.drawer.querySelector('[data-mn-drawer-panel="' + esc(idx) + '"]');
      var body = el.querySelector('[data-mn-drawer-body]');
      if (dpanel && body) {
        slice(dpanel.querySelectorAll(':scope > [data-mn-drawer-body]:not([data-mn-from])')).forEach(function (b) { b.hidden = true; b.setAttribute('data-mn-replaced', sid); });
        slice(this.drawer.querySelectorAll('[data-mn-drawer-panel^="' + esc(idx) + '-"]:not([data-mn-from])')).forEach(function (p) { p.hidden = true; p.setAttribute('data-mn-replaced', sid); });
        body.setAttribute('data-mn-from', sid);
        dpanel.appendChild(body);
        var container = this.drawer.querySelector('[data-mn-drawer-panels]');
        var subs = slice(body.querySelectorAll('[data-mn-drawer-subpanels] > [data-mn-drawer-panel]')).concat(slice(el.querySelectorAll(':scope > [data-mn-drawer-panel]')));
        subs.forEach(function (p) {
          p.setAttribute('data-mn-from', sid);
          container.appendChild(p);
        });
        // Root row must be a drill button now that the item has content
        var row = this.drawer.querySelector('[data-mn-root-row="' + esc(idx) + '"]');
        var anchor = row && row.querySelector(':scope > a.mn__drawer-link');
        if (anchor) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'mn__drawer-link mn__drawer-link--drill';
          btn.setAttribute('data-mn-drill', idx);
          btn.setAttribute('data-mn-title', dpanel.getAttribute('data-mn-title') || '');
          btn.setAttribute('aria-expanded', 'false');
          btn.setAttribute('aria-controls', dpanel.id);
          btn.setAttribute('data-mn-from', sid);
          btn.setAttribute('data-mn-generated', '');
          btn.innerHTML = anchor.innerHTML + '<svg class="mn__icon mn__drawer-arrow" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><use href="#mn-i-chevron-right"/></svg>';
          anchor.hidden = true;
          anchor.setAttribute('data-mn-replaced', sid);
          row.appendChild(btn);
        }
        var rootLabel = row && row.querySelector('[data-mn-root-label]');
        if (rootLabel && label) {
          var txt = label.querySelector('.mn__link-text');
          if (txt) {
            if (!rootLabel.hasAttribute('data-mn-original')) rootLabel.setAttribute('data-mn-original', rootLabel.textContent);
            rootLabel.textContent = txt.textContent;
            rootLabel.setAttribute('data-mn-labelled', sid);
          }
          // The Dropdown's icon shows on the mobile main menu too, unless a "Menu item icon" block already put one there
          var icon = label.querySelector('.mn__icon:not(.mn__chevron)');
          if (icon && !row.querySelector('[data-mn-root-icon]')) {
            slice(row.querySelectorAll('[data-mn-root-label]')).forEach(function (target) {
              var slot = document.createElement('span');
              slot.className = 'mn__icon-slot mn__icon-slot--root';
              slot.setAttribute('data-mn-root-icon', '');
              slot.setAttribute('data-mn-from', sid);
              slot.setAttribute('data-mn-generated', '');
              slot.appendChild(icon.cloneNode(true));
              target.parentNode.insertBefore(slot, target);
            });
          }
          // Its badge too, in its colour, unless the menu title already gives the row one ("Sale [Offers]")
          var badge = label.querySelector('.mn__badge');
          if (badge && !row.querySelector('.mn__badge')) {
            slice(row.querySelectorAll('[data-mn-root-label]')).forEach(function (target) {
              var copy = badge.cloneNode(true);
              copy.insertAdjacentHTML('afterbegin', '<span class="mn__visually-hidden">, </span>');
              copy.setAttribute('data-mn-from', sid);
              copy.setAttribute('data-mn-generated', '');
              target.parentNode.insertBefore(copy, target.nextSibling);
            });
          }
          // And "Colour the item": the row takes the same colour as the bar item
          if (style && el.hasAttribute('data-mn-highlight')) {
            style.split(';').forEach(function (decl) { var i = decl.indexOf(':'); if (i > 0) row.style.setProperty(decl.slice(0, i).trim(), decl.slice(i + 1).trim()); });
            row.classList.add('mn__item--highlight');
            row.setAttribute('data-mn-styled', sid);
          }
        }
        slice(body.querySelectorAll('[data-mn-drawer-subpanels]')).forEach(function (w) { if (!w.children.length && w.parentNode) w.parentNode.removeChild(w); });
      }
    }

    this.layoutSplits(li);
    this.badgeText();
    el.setAttribute('data-mn-attached', 'true');
    return true;
  };

    /* A badge with its own colour gets white or near-black text, whichever contrasts more (WCAG) */
  MegaNav.prototype.badgeText = function () {
    slice(document.querySelectorAll('.mn__badge[style*="--mn-badge-bg"]')).forEach(function (b) {
      var c = (getComputedStyle(b).backgroundColor.match(/[\d.]+/g) || []).map(function (v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
      if (c.length < 3) return;
      var l = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
      b.style.setProperty('--mn-badge-text', 1.05 / (l + 0.05) >= (l + 0.05) / 0.0556 ? '#ffffff' : '#111111');
    });
  };

  /* "Split the links into" (desktop): rows = links / columns, rounded up (13 in 2 = 7 + 6) */
  MegaNav.prototype.layoutSplits = function (scope) {
    slice((scope || document).querySelectorAll('.mn__panel .mn__column-list[data-mn-split]:not(.mn__column-list--cards)')).forEach(function (ul) {
      var count = slice(ul.children).filter(function (row) { return row.firstElementChild || row.textContent.trim(); }).length;
      var cols = parseInt(ul.getAttribute('data-mn-split'), 10) || 1;
      ul.style.setProperty('--mn-split-rows', String(Math.max(1, Math.ceil(count / cols))));
      ul.classList.add('mn__column-list--split');
    });
  };

  MegaNav.prototype.detachSection = function (sid) {
    if (!sid) return;
    // Give the item section its content back (so it can be re-attached, e.g. after the core reloads in the editor)
    var home = document.querySelector('[data-mn-item-section][data-section-id="' + esc(sid) + '"]');
    slice(document.querySelectorAll('[data-mn-from="' + esc(sid) + '"]')).forEach(function (n) {
      if (home && !n.hasAttribute('data-mn-generated')) home.appendChild(n);
      else if (n.parentNode) n.parentNode.removeChild(n);
    });
    slice(document.querySelectorAll('[data-mn-replaced="' + esc(sid) + '"]')).forEach(function (n) {
      n.hidden = false;
      n.removeAttribute('data-mn-replaced');
    });
    slice(document.querySelectorAll('[data-mn-labelled="' + esc(sid) + '"]')).forEach(function (node) {
      if (node.hasAttribute('data-mn-root-label')) node.textContent = node.getAttribute('data-mn-original') || node.textContent;
      else node.innerHTML = node.getAttribute('data-mn-original') || node.innerHTML;
      node.removeAttribute('data-mn-labelled');
    });
    slice(document.querySelectorAll('[data-mn-styled="' + esc(sid) + '"]')).forEach(function (li) {
      li.style.removeProperty('--mn-item-color');
      li.classList.remove('mn__item--highlight');
      li.removeAttribute('data-mn-styled');
    });
    if (this.sections[sid]) {
      var key = this.sections[sid].key;
      var li = this.bar && this.bar.querySelector('[data-mn-item][data-mn-key="' + esc(key) + '"]');
      if (li) {
        var def = li.querySelector(':scope > [data-mn-panel]');
        var lnk = li.querySelector(':scope > [data-mn-link]');
        if (def) {
          li.classList.add('mn__item--has-panel');
          li.setAttribute('data-mn-layout', def.getAttribute('data-mn-default-layout') || 'columns');
          li.setAttribute('data-mn-width', def.getAttribute('data-mn-default-width') || 'full');
          li.classList.toggle('mn__item--anchored', li.getAttribute('data-mn-width') === 'anchored');
          var tg2 = li.querySelector(':scope > [data-mn-toggle]');
          if (tg2) { tg2.setAttribute('aria-expanded', 'false'); tg2.setAttribute('aria-controls', def.id); }
        } else {
          li.classList.remove('mn__item--has-panel', 'mn__item--anchored');
          li.removeAttribute('data-mn-layout');
          li.removeAttribute('data-mn-width');
          if (lnk) { lnk.removeAttribute('aria-expanded'); lnk.removeAttribute('aria-controls'); }
        }
      }
      delete this.sections[sid];
      if (this.settings.designMode) this.noteSections();
    }
    slice(document.querySelectorAll('[data-mn-item-section][data-section-id="' + esc(sid) + '"]')).forEach(function (el) {
      el.removeAttribute('data-mn-attached');
    });
  };

  MegaNav.prototype.hoistSubpanels = function () {
    if (!this.drawer) return;
    var container = this.drawer.querySelector('[data-mn-drawer-panels]');
    slice(this.drawer.querySelectorAll('[data-mn-drawer-subpanels] > [data-mn-drawer-panel]')).forEach(function (p) {
      container.appendChild(p);
    });
  };

  /* ------------------------------------------------------------------ */
  /* Deferred images: panels and drawer screens render <img data-src>;    */
  /* nothing downloads until a panel is about to be shown.                */
  /* ------------------------------------------------------------------ */

  /* Append UTM parameters to campaign links (promo tiles, Link blocks with a campaign) */
  MegaNav.prototype.tagLinks = function (el) {
    var utm = this.settings.utm;
    if (!utm || !el) return;
    slice(el.querySelectorAll('a[data-mn-campaign]:not([data-mn-tagged])')).forEach(function (a) {
      a.setAttribute('data-mn-tagged', '');
      var href = a.getAttribute('href');
      if (!href || /^(#|mailto:|tel:|javascript:)/i.test(href)) return;
      var campaign = a.getAttribute('data-mn-campaign');
      if (!campaign) return;
      var item = a.closest('[data-mn-item]');
      var content = a.getAttribute('data-mn-track-item') || (item && item.getAttribute('data-mn-key')) || (a.closest('[data-mn-drawer-panel]') && a.closest('[data-mn-drawer-panel]').getAttribute('data-mn-key')) || '';
      var url;
      try { url = new URL(href, window.location.href); } catch (e) { return; }
      if (url.origin !== window.location.origin) return;   // never tag links to another origin (includes //host paths)
      var params = url.searchParams;
      if (!params.has('utm_source')) params.set('utm_source', utm.source || 'mega-nav');
      if (!params.has('utm_medium')) params.set('utm_medium', utm.medium || 'navigation');
      if (!params.has('utm_campaign')) params.set('utm_campaign', campaign);
      if (content && !params.has('utm_content')) params.set('utm_content', content);
      a.setAttribute('href', url.pathname + url.search + url.hash);
    });
  };

  /* ahead = true: this screen is one tap away, not on show. Its pictures are fetched at low   */
  /* priority so they never compete with the page's own images. Every picture fades in as it  */
  /* arrives (data-mn-fade pending -> done), and a cached one is marked done at once.          */
  MegaNav.prototype.hydrate = function (el, ahead) {
    if (!el || el.getAttribute('data-mn-hydrated') === 'true') return;
    this.tagLinks(el);
    slice(el.querySelectorAll('img[data-src], img[data-srcset]')).forEach(function (img) {
      var ss = img.getAttribute('data-srcset');
      var src = img.getAttribute('data-src');
      var done = function () { img.setAttribute('data-mn-fade', 'done'); };
      if (ahead) img.setAttribute('fetchpriority', 'low');
      img.setAttribute('data-mn-fade', 'pending');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      if (ss) { img.setAttribute('srcset', ss); img.removeAttribute('data-srcset'); }
      if (src) { img.setAttribute('src', src); img.removeAttribute('data-src'); }
      if (img.complete && img.naturalWidth > 0) done();
    });
    el.setAttribute('data-mn-hydrated', 'true');
  };

    /* "Load the top pictures when the phone menu opens" (a Mega Nav setting, off by default): once */
  /* per page, when the browser is idle, at low priority, fetch the top of each item's screen:  */
  /* its top picture, full-width promos and the first four cards or tiles of its top row. The   */
  /* rest still loads as before. Skipped on a 2G connection or when the phone has Data Saver on. */
  MegaNav.prototype.preloadTops = function () {
    var self = this;
    var conn = navigator.connection;
    if (!this.settings.preloadTop || !this.drawer || this.topsLoaded) return;
    if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return;
    this.topsLoaded = true;
    var run = function () {
      slice(self.drawer.querySelectorAll('[data-mn-drawer-panel][data-mn-parent="root"]')).forEach(function (panel) {
        // A screen can hold a hidden copy from a Dropdown section that another one replaced: skip those
        var bodies = slice(panel.querySelectorAll(':scope > [data-mn-drawer-body]')).filter(function (b) { return !b.hidden; });
        (bodies.length ? bodies : [panel]).forEach(function (body) {
          var parts = slice(body.querySelectorAll('.mn__drawer-hero, .mn__drawer-full--top .mn__tile'));
          var row = slice(body.querySelectorAll('[data-mn-strip] .mn__column-list > li, [data-mn-strip] .mn__tile')).slice(0, 4);
          parts.concat(row).forEach(function (el) { self.hydrate(el, true); });
        });
      });
    };
    if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 2000 }); else setTimeout(run, 300);
  };

  /* One step ahead: once a screen is open, fetch the pictures of the screens one tap away   */
  /* from it, at low priority and only when the browser is idle. Never two taps away, never  */
  /* other departments. Skipped on a 2G connection or when the phone has Data Saver on.      */
  MegaNav.prototype.prefetchAhead = function (id) {
    var self = this;
    var conn = navigator.connection;
    if (!this.drawer || !id) return;
    if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return;
    var panels = slice(this.drawer.querySelectorAll('[data-mn-drawer-panel][data-mn-parent="' + esc(id) + '"]'));
    if (!panels.length) return;
    var run = function () { panels.forEach(function (p) { self.hydrate(p, true); }); };
    if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 1500 }); else setTimeout(run, 150);
  };

  MegaNav.prototype.init = function () {
    this.resolvePlacement();
    this.setupMode();
    this.mountBar();
    this.hideThemeElements();
    if (this.applyMode) this.applyMode();   // now that the bar is mounted, the theme's header can decide the mode
    this.markOverflow();
    this.watchRemoval();
    this.noteEditor();
    this.attachAll();
    this.badgeText();
    this.bindGlobal();
    this.bindDesktop();
    this.bindDrawer();
    this.bindGestures();
    this.bindAnalytics();
    this.bindOpens();
    this.bindEditor();
    this.emit('meganav:ready');
  };

  /* ------------------------------------------------------------------ */
  /* Touch gestures: swipe back / swipe to close in the drawer            */
  /* ------------------------------------------------------------------ */
  MegaNav.prototype.bindGestures = function () {
    var self = this;
    if (!this.drawer) return;
    var start = null;
    this.on(this.drawer, 'touchstart', function (e) {
      // A finger on a row that opens a screen: start that screen's pictures now, before the tap lands
      var drill = e.target.closest && e.target.closest('[data-mn-drill]');
      if (drill) self.hydrate(self.drawerPanel(drill.getAttribute('data-mn-drill')));
      if (e.touches.length !== 1) { start = null; return; }
      if (e.target.closest && e.target.closest('.mn__strip, input, textarea')) { start = null; return; }
      start = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() };
    }, { passive: true });
    this.on(this.drawer, 'touchend', function (e) {
      if (!start) return;
      var touch = e.changedTouches && e.changedTouches[0];
      if (!touch) return;
      var dx = touch.clientX - start.x, dy = touch.clientY - start.y, dt = Date.now() - start.t;
      start = null;
      if (dt > 600 || Math.abs(dy) > 50 || Math.abs(dx) < 70) return;
      var rtl = self.isRtl();
      var side = self.settings.drawerSide === 'right' ? 'right' : 'left';
      if (rtl) side = side === 'left' ? 'right' : 'left';
      var away = side === 'left' ? dx < 0 : dx > 0;   // swiping towards the edge the drawer came from
      if (away) {
        if (self.drawerStack.length > 1) self.drillBack(); else self.closeDrawer();
      }
    }, { passive: true });
  };

  /* ------------------------------------------------------------------ */
  /* Analytics: one event for every navigation click                     */
  /* ------------------------------------------------------------------ */
  MegaNav.prototype.bindAnalytics = function () {
    var self = this;
    var handler = function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[data-mn-track]') : null;
      if (!a) return;
      if (self.flushOpen) self.flushOpen(a);
      var detail = {
        kind: a.getAttribute('data-mn-track'),
        title: a.getAttribute('data-mn-track-title') || (a.textContent || '').trim(),
        href: a.getAttribute('href'),
        item: a.getAttribute('data-mn-track-item') || (a.closest('[data-mn-item]') && a.closest('[data-mn-item]').getAttribute('data-mn-key')) || (a.closest('[data-mn-key]') && a.closest('[data-mn-key]').getAttribute('data-mn-key')) || null,
        column: a.getAttribute('data-mn-track-column') || (a.closest('[data-mn-column]') && a.closest('[data-mn-column]').getAttribute('data-mn-column')) || null,
        surface: a.closest('[data-mn-drawer]') ? 'drawer' : 'desktop'
      };
      self.emit('meganav:click', detail);
      var mode = self.settings.trackMode || 'datalayer';
      var name = self.settings.trackEvent || 'meganav_click';
      if (mode === 'off') return;
      if ((mode === 'datalayer' || mode === 'all') && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: name, meganav: detail });
      }
      if ((mode === 'gtag' || mode === 'all') && typeof window.gtag === 'function') {
        window.gtag('event', name, { nav_kind: detail.kind, nav_title: detail.title, nav_href: detail.href, nav_item: detail.item, nav_column: detail.column, nav_surface: detail.surface });
      }
      if ((mode === 'shopify' || mode === 'all') && window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        try { window.Shopify.analytics.publish(name, detail); } catch (err) { /* pixels not ready */ }
      }
    };
    if (this.bar) this.on(this.bar, 'click', handler);
    if (this.drawer) this.on(this.drawer, 'click', handler);
  };

    /* One meganav_open per menu opened, with the same six fields as meganav_click (docs/tracking.md). Phone:
     the menu, at once. Desktop: a dropdown, once it has stayed open a second or something in it is clicked,
     so a mouse passing over the bar is not counted */
  MegaNav.prototype.bindOpens = function () {
    var self = this, wait = null, waiting = null, counted = null;
    var send = function (li) {
      clearTimeout(wait);
      waiting = null;
      counted = li;
      var top = li && li.querySelector('[data-mn-track="top"]');
      var d = { kind: 'open', title: li ? (top && top.getAttribute('data-mn-track-title')) || li.getAttribute('data-mn-key') : 'Menu', href: null, item: li ? li.getAttribute('data-mn-key') : null, column: null, surface: li ? 'desktop' : 'drawer' };
      var mode = self.settings.trackMode || 'datalayer';
      if ((mode === 'datalayer' || mode === 'all') && Array.isArray(window.dataLayer)) window.dataLayer.push({ event: 'meganav_open', meganav: d });
      if ((mode === 'gtag' || mode === 'all') && typeof window.gtag === 'function') window.gtag('event', 'meganav_open', { nav_kind: d.kind, nav_title: d.title, nav_href: d.href, nav_item: d.item, nav_column: d.column, nav_surface: d.surface });
      if ((mode === 'shopify' || mode === 'all') && window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        try { window.Shopify.analytics.publish('meganav_open', d); } catch (err) { /* pixels not ready */ }
      }
    };
    this.flushOpen = function (a) { if (waiting && waiting.contains(a)) send(waiting); };
    this.on(this.root, 'meganav:open', function (e) {
      var li = e.detail && e.detail.item;
      if (!li || li === counted || li === waiting) return;
      clearTimeout(wait);
      waiting = li;
      wait = setTimeout(function () { send(li); }, 1000);
    });
    this.on(this.root, 'meganav:close', function (e) {
      var li = e.detail && e.detail.item;
      if (li === waiting) { clearTimeout(wait); waiting = null; }
      if (li === counted) counted = null;
    });
    this.on(this.root, 'meganav:drawer:open', function () { send(null); });
  };

  MegaNav.prototype.on = function (target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.listeners.push([target, type, handler, options]);
  };

  MegaNav.prototype.emit = function (name, detail) {
    var evt;
    try { evt = new CustomEvent(name, { bubbles: true, detail: detail || { instance: this } }); }
    catch (e) { evt = document.createEvent('CustomEvent'); evt.initCustomEvent(name, true, false, detail || { instance: this }); }
    this.root.dispatchEvent(evt);
  };

  MegaNav.prototype.setupMode = function () {
    var self = this;
    this.mql = window.matchMedia ? window.matchMedia('(min-width: ' + this.settings.breakpoint + 'px)') : { matches: true, addListener: function () {}, removeListener: function () {} };
    var apply = function () {
      var next = self.computeMode();
      if (next === self.mode && self.root.getAttribute('data-mn-mode') === next) return;
      self.mode = next;
      self.root.setAttribute('data-mn-mode', next);
      if (self.bar) self.bar.setAttribute('data-mn-mode', next);
      if (next === 'mobile') self.closeAll(); else self.closeDrawer();
    };
    this.mode = null;
    apply();
    if (this.mql.addEventListener) this.mql.addEventListener('change', apply); else this.mql.addListener(apply);
    this.mqlHandler = apply;
    this.applyMode = apply;
  };

    /* "When the bar doesn't fit" (a Mega Nav setting, off by default). With the bar on show, measure it: */
  /* try one tighter step, then a second, and if the items still do not fit, fold the bar into our own  */
  /* burger and the phone menu. Returns the mode to use; with the setting off it returns what it was given. */
  MegaNav.prototype.fitBar = function (mode) {
    var bar = this.bar, list = bar && bar.querySelector('[data-mn-list]');
    if (!this.settings.barFit || !bar || !list) return mode;
    bar.classList.remove('mn__bar--fit-1', 'mn__bar--fit-2', 'mn__bar--folded');
    if (mode !== 'desktop') return mode;
    bar.classList.add('mn__bar--fit');
    var prevRoot = this.root.getAttribute('data-mn-mode'), prevBar = bar.getAttribute('data-mn-mode');
    this.root.setAttribute('data-mn-mode', 'desktop');   // the list must be on show to be measured
    bar.setAttribute('data-mn-mode', 'desktop');
    var fits = function () { return list.scrollWidth <= list.clientWidth + 1; };
    var ok = fits();
    if (!ok) { bar.classList.add('mn__bar--fit-1'); ok = fits(); }
    if (!ok) { bar.classList.remove('mn__bar--fit-1'); bar.classList.add('mn__bar--fit-2'); ok = fits(); }
    if (!ok) { bar.classList.remove('mn__bar--fit-2'); bar.classList.add('mn__bar--folded'); }
    if (prevRoot) this.root.setAttribute('data-mn-mode', prevRoot);
    if (prevBar) bar.setAttribute('data-mn-mode', prevBar); else bar.removeAttribute('data-mn-mode');
    return ok ? 'desktop' : 'mobile';
  };


  /* Desktop or mobile. With a theme's own header in play, what the theme shows wins over our breakpoint. */
  MegaNav.prototype.computeMode = function () {
    var s = this.settings, q = function (sel) { if (!sel) return null; try { return document.querySelector(sel); } catch (e) { return null; } };
    var shown = function (el) { return !!(el && el.getClientRects().length); };
    var mode = null, triggerShown = false;
    if (s.placement === 'mount' && this.mounted) {
      var trigger = q(s.mobileTriggerSelector);
      triggerShown = shown(trigger);
      if (triggerShown) mode = 'mobile';
      else {
        // The slot the theme gives its desktop menu: the mount target, or the bar's parent when we sit beside a hidden target
        var slot = s.mountMode === 'after' ? (this.bar && this.bar.parentNode) : this.mountTarget;
        if (slot && shown(slot) && this.bar && this.bar.getClientRects().length) mode = 'desktop';
      }
    }
    if (!mode) mode = this.mql.matches ? 'desktop' : 'mobile';
    mode = this.fitBar(mode);
    var nodes = [this.root, this.bar];
    nodes.forEach(function (n) { if (n) n.classList.toggle('mn--trigger-selector', triggerShown); });
    s.mobileTrigger = triggerShown ? 'selector' : 'own';
    this.wireTriggers();
    this.placeBurger(mode);
    return mode;
  };

  /* The theme's burger(s) are openers of our drawer; (re)apply the ARIA wiring whenever the DOM may have changed */
  MegaNav.prototype.wireTriggers = function () {
    var s = this.settings, self = this;
    if (!s.mobileTriggerSelector || !this.drawer) return;
    var nodes;
    try { nodes = document.querySelectorAll(s.mobileTriggerSelector); } catch (e) { nodes = []; }
    this.openers = (this.openers || []).filter(function (el) { return el.closest && el.closest('[data-mega-nav], [data-mn-bar]'); });
    slice(nodes).forEach(function (el) {
      self.openers.push(el);
      if (!self.drawerOpen) self.rememberTriggerLook(el);
      if (self.drawer.id) el.setAttribute('aria-controls', self.drawer.id);
      if (!el.hasAttribute('aria-expanded')) el.setAttribute('aria-expanded', self.drawerOpen ? 'true' : 'false');
    });
  };

  /* The theme's burger is drawn by the theme: some swap the icon on aria-expanded, some on a class their
     own script toggles, some on the <details> around a <summary>. We block that script's click handler,
     but a theme that still flips its button, or re-renders it while our drawer is open, must not be
     left showing an X once we close. So: remember how the button looked while closed and put it back. */
  MegaNav.prototype.rememberTriggerLook = function (el) {
    if (!el || el._mnLook || el.hasAttribute('data-mn-drawer-open')) return;
    var details = el.closest ? el.closest('details') : null;
    el._mnLook = { cls: el.className, details: details, detailsCls: details ? details.className : '' };
  };

  MegaNav.prototype.restoreTriggerLook = function (el) {
    var look = el._mnLook;
    var details = el.closest ? el.closest('details') : null;
    if (details) details.removeAttribute('open');
    if (!look) return;
    if (el.className !== look.cls) el.className = look.cls;
    if (look.details && look.details === details && details.className !== look.detailsCls) details.className = look.detailsCls;
  };

  /* Every trigger currently on the page, not only those seen at start-up: Horizon and Dawn re-render the
     header after first paint, and a list taken earlier would miss the button the shopper actually taps */
  MegaNav.prototype.themeTriggers = function () {
    var sel = this.settings.mobileTriggerSelector;
    if (!sel) return [];
    try { return slice(document.querySelectorAll(sel)); } catch (e) { return []; }
  };

  /* Our own burger lives in the bar; when the bar sits inside a theme slot the theme hides on mobile, move it to the section root */
  MegaNav.prototype.placeBurger = function (mode) {
    if (!this.bar) return;
    var burger = this.burger || (this.burger = this.bar.querySelector('[data-mn-drawer-open]') || this.root.querySelector('[data-mn-drawer-open]'));
    if (!burger) return;
    var barHidden = this.mounted && !this.bar.getClientRects().length;
    var standalone = mode === 'mobile' && this.settings.mobileTrigger === 'own' && barHidden;
    if (standalone && burger.parentNode !== this.root) {
      burger.classList.add('mn__burger--standalone');
      this.root.insertBefore(burger, this.root.firstChild);
    } else if (!standalone && burger.parentNode === this.root) {
      burger.classList.remove('mn__burger--standalone');
      var inner = this.bar.querySelector('.mn__bar-inner') || this.bar;
      inner.insertBefore(burger, inner.firstChild);
    }
  };

  /* Turn the 'Position' setting into concrete selectors. */
  MegaNav.prototype.resolvePlacement = function () {
    var s = this.settings;
    var theme = s.theme || 'auto';
    var q = function (sel) { if (!sel) return null; try { return document.querySelector(sel); } catch (e) { return null; } };
    this.detectedTheme = null;
    this.placementIssue = '';
    if (theme === 'row') {
      s.placement = 'bar'; s.mountSelector = ''; s.mobileTriggerSelector = ''; s.hideSelector = '';
    } else if (theme === 'custom') {
      s.placement = s.mountSelector ? 'mount' : 'bar';
      if (s.mountSelector && !q(s.mountSelector)) { this.placementIssue = 'custom'; s.placement = 'bar'; document.documentElement.classList.add('mn-fallback'); }
    } else {
      var names = theme === 'auto' ? MegaNav.THEME_ORDER : [theme];
      var preset = null, spot = null;
      for (var i = 0; i < names.length && !preset; i++) {
        var p = MegaNav.THEMES[names[i]];
        if (!p) continue;
        var tries = [{ mount: p.mount, mode: p.mode, hide: p.hide }].concat(p.alts || []);
        var present = null;
        for (var j = 0; j < tries.length; j++) {
          var el = q(tries[j].mount);
          if (!el) continue;
          if (!present) present = tries[j];
          // Prefer a slot the theme is actually showing: a header set to its drawer menu style keeps
          // the inline menu in the DOM but hidden, and draws the real one somewhere else. An element
          // we hid ourselves still counts, or re-resolving after the theme re-renders its header
          // would walk away from the slot we are already using.
          if (el.getClientRects().length || el.hasAttribute('data-mn-hidden')) { spot = tries[j]; break; }
        }
        if (!spot && present) spot = present;
        if (spot) { preset = p; this.detectedTheme = names[i]; }
      }
      if (preset) {
        s.placement = 'mount'; s.mountSelector = spot.mount; s.mountMode = spot.mode; s.mobileTriggerSelector = preset.trigger; s.hideSelector = spot.hide === undefined ? preset.hide : spot.hide;
      } else {
        s.placement = 'bar'; s.mountSelector = ''; s.mobileTriggerSelector = ''; s.hideSelector = '';
        this.placementIssue = theme === 'auto' ? 'auto' : 'theme';
        document.documentElement.classList.add('mn-fallback');
      }
    }
    s.mobileTrigger = s.mobileTriggerSelector && q(s.mobileTriggerSelector) ? 'selector' : 'own';
    this.setPlacement(s.placement);
  };

  /* Themes that re-render their header (section rendering API) delete the mounted bar: put it back. */
  MegaNav.prototype.watchRemoval = function () {
    if (!window.MutationObserver || !this.mounted) return;
    var self = this;
    var connected = function (n) { return typeof n.isConnected === 'boolean' ? n.isConnected : document.contains(n); };
    this.mo = new MutationObserver(function () {
      if (!self.mounted || !self.bar || connected(self.bar) || self.remounting) return;
      self.remounting = true;
      requestAnimationFrame(function () {
        self.remounting = false;
        self.mounted = false; self.mountStash = null; self.mountTarget = null; self.fixedContainer = null;
        // A theme that re-renders its header by rewriting innerHTML leaves a dead copy of our bar
        // behind. Ours is the one we hold a reference to; anything else with our owner id is a clone.
        slice(document.querySelectorAll('[data-mn-bar][data-mn-owner="' + esc(self.settings.id) + '"]')).forEach(function (el) {
          if (el !== self.bar && el.parentNode) el.parentNode.removeChild(el);
        });
        self.resolvePlacement();
        self.mountBar();
        self.hideThemeElements();
        self.attachAll();
        self.markOverflow();
        if (self.applyMode) self.applyMode();
      });
    });
    this.mo.observe(document.body, { childList: true, subtree: true });
  };

  MegaNav.prototype.setPlacement = function (placement) {
    var s = this.settings;
    s.placement = placement;
    var nodes = [this.root, this.bar];
    nodes.forEach(function (n) {
      if (!n) return;
      n.classList.remove('mn--placement-mount', 'mn--placement-bar');
      n.classList.add('mn--placement-' + placement);
      n.classList.toggle('mn--trigger-selector', s.mobileTrigger === 'selector');
    });
  };

  /* Theme Editor only: fill in the status note rendered by the section (position + hand-designed dropdowns). */
  MegaNav.prototype.noteEditor = function () {
    if (!this.settings.designMode || !this.root) return;
    var s = this.settings, text = '';
    var themeName = this.detectedTheme ? MegaNav.THEMES[this.detectedTheme].label : '';
    if (this.mounted) {
      text = 'Sitting inside your theme\u2019s header' + (themeName ? ' (' + themeName + ')' : '') +
        (s.mobileTrigger === 'selector' ? '. The theme\u2019s menu button opens our mobile menu.' : '. Our own button opens the mobile menu.');
    } else if (this.placementIssue === 'auto') {
      text = 'Could not find your theme\u2019s menu, so the bar is in its own row. Happy with that? Set Position to \u201cIts own row\u201d. Otherwise pick your theme, or use Custom selectors.';
    } else if (this.placementIssue === 'theme') {
      text = 'Could not find the ' + MegaNav.THEMES[s.theme].label + ' menu on this page, so the bar is in its own row.';
    } else if (this.placementIssue === 'hidden') {
      text = 'Your theme hides its menu at this size, so the bar is in its own row here. Pick \u201cIts own row\u201d under Position if that is what you want.';
    } else if (this.placementIssue === 'custom' || this.placementIssue === 'mount') {
      text = 'Could not find \u201c' + (s.mountSelector || '') + '\u201d, so the bar is in its own row.';
    } else {
      text = 'Showing in its own row, where this section sits.';
    }
    var note = this.root.querySelector('[data-mn-editor-note]');
    if (!note) {
      note = document.createElement('details');
      note.className = 'mn__editor-note';
      note.setAttribute('data-mn-editor-note', '');
      note.innerHTML = '<summary class="mn__editor-note-summary"><strong>Mega Nav</strong><span data-mn-note-position-short></span><span data-mn-note-sections-short></span><span class="mn__editor-note-more">Details</span></summary><ul class="mn__editor-note-list" role="list"><li data-mn-note-position></li><li data-mn-note-sections hidden></li></ul>';
      this.root.insertBefore(note, this.root.firstChild);
    }
    this.editorNote = note;
    var pos = note.querySelector('[data-mn-note-position]');
    if (pos) pos.textContent = text;
    var short = note.querySelector('[data-mn-note-position-short]');
    if (short) short.textContent = this.mounted ? 'inside the header' + (themeName ? ' (' + themeName + ')' : '') : (this.placementIssue ? 'own row (theme menu not found)' : 'own row');
    this.noteSections();
  };

  MegaNav.prototype.noteSections = function () {
    if (!this.settings.designMode || !this.editorNote) return;
    var li = this.editorNote.querySelector('[data-mn-note-sections]');
    if (!li) return;
    var self = this, names = [];
    Object.keys(this.sections).forEach(function (sid) {
      var item = self.bar && self.bar.querySelector('[data-mn-item][data-mn-key="' + esc(self.sections[sid].key) + '"] [data-mn-link]');
      names.push(item ? item.textContent.replace(/\s+/g, ' ').trim() : self.sections[sid].key);
    });
    var dups = Object.keys(this.duplicateKeys || {});
    li.textContent = (names.length
      ? 'Hand-designed dropdowns: ' + names.join(', ') + '. Every other item uses the automatic dropdown.'
      : 'No hand-designed dropdowns yet. Every item uses the automatic dropdown. To design one, add a \u201cMega Nav \u00b7 Dropdown\u201d section.')
      + (dups.length ? ' Two Dropdown sections point at the same item (' + dups.join(', ') + '); only the newest is used. Remove one.' : '')
      + (this.menuMismatch && Object.keys(this.menuMismatch).length
        ? ' A Dropdown section is reading the menu \u201c' + Object.keys(this.menuMismatch).join('\u201d, \u201c') + '\u201d while the bar uses \u201c' + (this.settings.menuTitle || this.settings.menu) + '\u201d, so its groups look empty. Set Menu (Advanced) on that section to the same menu.'
        : '')
      + ' Mega Nav ' + MegaNav.version + '.';
    li.hidden = false;
    var short = this.editorNote.querySelector('[data-mn-note-sections-short]');
    if (short) short.textContent = names.length + ' hand-designed dropdown' + (names.length === 1 ? '' : 's');
  };

  MegaNav.prototype.mountBar = function () {
    var s = this.settings;
    if (this.bar && s.placement === 'mount' && s.mountSelector) {
      var target = null;
      try { target = document.querySelector(s.mountSelector); } catch (e) { target = null; }
      var hiddenTarget = target && s.mountMode === 'replace' && !target.getClientRects().length && this.mql && this.mql.matches;
      if (!target || target.closest('[data-mega-nav]') || hiddenTarget) {
        this.placementIssue = this.placementIssue || (hiddenTarget ? 'hidden' : 'mount');
        this.setPlacement('bar');
        // We are not replacing the theme's menu after all, so it must not stay hidden
        this.settings.hideSelector = '';
        this.showThemeElements();
        document.documentElement.classList.add('mn-fallback');
      } else {
        var th = target.getBoundingClientRect().height;
        if (th > 0 && th < 120) this.bar.style.minHeight = Math.round(th) + 'px';
        if (s.mountMode === 'prepend') target.insertBefore(this.bar, target.firstChild);
        else if (s.mountMode === 'append') target.appendChild(this.bar);
        else if (s.mountMode === 'after') target.parentNode.insertBefore(this.bar, target.nextSibling);
        else {
          this.mountStash = document.createDocumentFragment();
          while (target.firstChild) this.mountStash.appendChild(target.firstChild);
          target.appendChild(this.bar);
        }
        this.mountTarget = target;
        this.mounted = true;
        var outerNav = this.bar.parentNode && this.bar.parentNode.closest && this.bar.parentNode.closest('nav, [role="navigation"]');
        if (outerNav) {
          this.bar.setAttribute('data-mn-aria-label', this.bar.getAttribute('aria-label') || '');
          this.bar.removeAttribute('aria-label');
          this.bar.setAttribute('role', 'presentation');
        }
        this.fixedContainer = this.findFixedContainer(target);
        this.root.classList.add('mn--mounted');
        this.bar.classList.add('mn__bar--mounted');
        this.bar.setAttribute('data-mn-mode', this.mode);
      }
    }
    if (this.drawer && this.drawer.parentNode !== document.body) document.body.appendChild(this.drawer);
    if (this.drawerOverlay && this.drawerOverlay.parentNode !== document.body) document.body.appendChild(this.drawerOverlay);
    // The page overlay stays next to the bar so they share a stacking context (sticky headers, mounted mode)
    if (this.overlay && this.mounted && this.bar.parentNode && !this.fixedContainer) this.bar.parentNode.insertBefore(this.overlay, this.bar);
    if (this.fixedContainer) this.overlayDisabled = true;
  };

  /* An ancestor with transform/filter/perspective/contain turns position:fixed into
     "fixed to that ancestor". Find it so full-width panels can be positioned against it. */
  MegaNav.prototype.findFixedContainer = function (el) {
    var node = el;
    while (node && node !== document.documentElement) {
      var cs = window.getComputedStyle(node);
      if ((cs.transform && cs.transform !== 'none') || (cs.filter && cs.filter !== 'none') || (cs.perspective && cs.perspective !== 'none') ||
          (cs.willChange && /transform|perspective|filter/.test(cs.willChange)) || (cs.contain && /paint|layout|strict|content/.test(cs.contain)) ||
          (cs.backdropFilter && cs.backdropFilter !== 'none') || (cs.containerType && cs.containerType !== 'normal')) return node;
      node = node.parentElement;
    }
    return null;
  };

  MegaNav.prototype.hideThemeElements = function () {
    var sel = this.settings.hideSelector;
    if (!sel) return;
    /* A stylesheet rule, not just inline styles. Horizon re-renders parts of its header after first
       paint (Section Rendering), which replaces the elements we styled and brings the theme's own
       menu back. A rule keeps applying to whatever the theme puts there next. :is() wraps the
       selector so a comma inside it cannot escape the :not() guards. */
    if (!this.hideStyle) {
      this.hideStyle = document.createElement('style');
      this.hideStyle.setAttribute('data-mn-hide-theme', this.settings.id || '');
      document.head.appendChild(this.hideStyle);
    }
    this.hideStyle.textContent = ':is(' + sel + '):not(.mn__bar):not([data-mega-nav]):not(:has(.mn__bar)) { display: none !important; }';
    var bar = this.bar, nodes;
    try { nodes = document.querySelectorAll(sel); } catch (e) { return; }
    var own = [this.bar, this.drawer, this.overlay, this.drawerOverlay];
    slice(nodes).forEach(function (el) {
      if (el.closest('[data-mega-nav]')) return;
      if (bar && (el === bar || el.contains(bar))) return;
      if (own.some(function (o) { return o && (o === el || el.contains(o)); })) return;
      el.setAttribute('data-mn-hidden', '');
      el.style.setProperty('display', 'none', 'important');
    });
  };

  /* -------------------------------------------------------------- global */
  MegaNav.prototype.bindGlobal = function () {
    var self = this;
    this.on(document, 'pointerdown', function (e) {
      self.lastPointerType = e.pointerType || 'mouse';
      var tab = e.target && e.target.closest && e.target.closest('[data-mn-tab]');
      // On touch, focus shows the tab before the click arrives: remember it so that click does not navigate
      self.touchTab = tab && self.lastPointerType !== 'mouse' && !tab.classList.contains('is-active') ? tab : null;
      var item = e.target && e.target.closest && e.target.closest('[data-mn-item][data-mn-layout]');
      if (item && self.bar && self.bar.contains(item)) self.hydrate(self.panelOf(item));
    }, { capture: true, passive: true });
    this.on(document, 'click', function (e) {
      if (self.openItem && self.bar && !self.bar.contains(e.target)) self.closeAll();
    });
    this.on(document, 'keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (self.drawerOpen) self.closeDrawer();
      else if (self.openItem) {
        var link = self.openItem.querySelector('[data-mn-link]');
        self.closeAll();
        if (link) link.focus();
      }
    });
    if (this.overlay) this.on(this.overlay, 'click', function () { self.closeAll(); });
    if (this.drawerOverlay) this.on(this.drawerOverlay, 'click', function () { self.closeDrawer(); });
    var reposition = function () {
      if (self.raf) return;
      self.raf = requestAnimationFrame(function () { self.raf = null; if (self.openItem) self.positionPanel(self.openItem); });
    };
    this.on(window, 'scroll', function () { if (self.openItem && self.mounted) reposition(); }, { passive: true });
    var list = this.bar && this.bar.querySelector('[data-mn-list]');
    if (list) this.on(list, 'scroll', function () { if (self.openItem) reposition(); }, { passive: true });
    var resizing = null;
    this.on(window, 'resize', function () {
      if (resizing) return;
      resizing = requestAnimationFrame(function () {
        resizing = null;
        if (self.applyMode) self.applyMode();
        self.markOverflow();
        if (self.mode !== 'desktop') return;
        if (self.openItem) reposition();
        if (self.mounted && self.mountTarget) self.fixedContainer = self.findFixedContainer(self.mountTarget);
      });
    });
  };

  /* ------------------------------------------------------------- desktop */
  MegaNav.prototype.bindDesktop = function () {
    var self = this;
    var bar = this.bar;
    if (!bar) return;

    var itemOf = function (t) { var i = t && t.closest && t.closest('[data-mn-item]'); return i && bar.contains(i) ? i : null; };

    this.on(bar, 'pointerover', function (e) {
      if (!self.isHoverable(e)) return;
      var item = itemOf(e.target);
      var tab = e.target.closest && e.target.closest('[data-mn-tab]');
      if (tab) {
        clearTimeout(self.timers.tab);
        var delay = 80;
        if (self.pointerTrail && self.pointerTrail.length > 1) {
          var a = self.pointerTrail[0], b = self.pointerTrail[self.pointerTrail.length - 1];
          var rtl = self.isRtl();
          var towardsPanel = rtl ? (a.x - b.x) : (b.x - a.x);
          if (towardsPanel > 12 && Math.abs(b.y - a.y) < towardsPanel * 1.5) delay = 320;
        }
        self.timers.tab = setTimeout(function () { self.activateTab(tab.closest('[data-mn-panel]'), tab); }, delay);
      }
      if (!item || (e.relatedTarget && item.contains(e.relatedTarget))) return;
      if (item.hasAttribute('data-mn-layout')) { self.hydrate(self.panelOf(item)); self.scheduleOpen(item); }
      else if (self.openItem) self.scheduleClose();
    });

    this.on(bar, 'pointermove', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      var trail = self.pointerTrail || (self.pointerTrail = []);
      trail.push({ x: e.clientX, y: e.clientY, t: e.timeStamp });
      while (trail.length > 6) trail.shift();
    }, { passive: true });

    this.on(bar, 'pointerout', function (e) {
      if (!self.isHoverable(e)) return;
      var tab = e.target.closest && e.target.closest('[data-mn-tab]');
      if (tab) clearTimeout(self.timers.tab);
      var item = itemOf(e.target);
      if (!item || (e.relatedTarget && item.contains(e.relatedTarget))) return;
      self.scheduleClose();
    });

    this.on(bar, 'click', function (e) {
      if (!e.target || !e.target.closest) return;
      var toggle = e.target.closest('[data-mn-toggle]');
      if (toggle) {
        e.preventDefault();
        var it = itemOf(toggle);
        if (!it || !it.hasAttribute('data-mn-layout')) return;
        if (self.openItem === it) self.closeAll(); else self.open(it, { focus: true });
        return;
      }
      var tab = e.target.closest('[data-mn-tab]');
      if (tab) {
        var touchTab = self.lastPointerType === 'touch' || self.lastPointerType === 'pen';
        if (touchTab && (self.touchTab === tab || !tab.classList.contains('is-active'))) {
          e.preventDefault();
          self.activateTab(tab.closest('[data-mn-panel]'), tab);
        }
        self.touchTab = null;
        return;
      }
      var link = e.target.closest('[data-mn-link]');
      if (link && self.mode === 'desktop') {
        var item = itemOf(link);
        if (!item || !item.hasAttribute('data-mn-layout')) return;
        var touch = self.lastPointerType === 'touch' || self.lastPointerType === 'pen';
        var clickMode = self.settings.openOn === 'click';
        if (self.openItem !== item && (clickMode || (touch && self.settings.touchFirstTapOpens))) {
          e.preventDefault();
          self.open(item);
        }
      }
    });

    this.on(bar, 'focusin', function (e) {
      var tab = e.target.closest && e.target.closest('[data-mn-tab]');
      if (tab) self.activateTab(tab.closest('[data-mn-panel]'), tab);
    });

    this.on(bar, 'keydown', function (e) {
      if (self.mode !== 'desktop') return;
      var t = e.target;
      var link = t.closest && t.closest('[data-mn-link]');
      var tab = t.closest && t.closest('[data-mn-tab]');
      if (link) {
        var item = itemOf(link);
        var hasPanel = item && item.hasAttribute('data-mn-layout');
        if ((e.key === 'ArrowDown' || e.key === ' ') && hasPanel) { e.preventDefault(); self.open(item, { focus: true }); return; }
        if (e.key === 'ArrowUp') { e.preventDefault(); self.closeAll(); return; }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
          var links = slice(bar.querySelectorAll('[data-mn-link]'));
          var i = links.indexOf(link);
          var forward = e.key === (self.isRtl() ? 'ArrowLeft' : 'ArrowRight');
          var next = e.key === 'Home' ? links[0] : e.key === 'End' ? links[links.length - 1] : forward ? links[i + 1] || links[0] : links[i - 1] || links[links.length - 1];
          if (!next) return;
          e.preventDefault();
          next.focus();
          if (self.openItem) {
            var ni = itemOf(next);
            if (ni && ni.hasAttribute('data-mn-layout')) self.open(ni); else self.closeAll();
          }
        }
        return;
      }
      if (tab) {
        var panel = tab.closest('[data-mn-panel]');
        var tabs = slice(panel.querySelectorAll('[data-mn-tab]'));
        var ti = tabs.indexOf(tab);
        if (e.key === 'ArrowDown' && tabs[ti + 1]) { e.preventDefault(); tabs[ti + 1].focus(); }
        if (e.key === 'ArrowUp' && tabs[ti - 1]) { e.preventDefault(); tabs[ti - 1].focus(); }
        if (e.key === 'ArrowRight') {
          var tp = panel.querySelector('[data-mn-tabpanel="' + esc(tab.getAttribute('data-mn-tab')) + '"]');
          var first = tp && tp.querySelector(FOCUSABLE);
          if (first) { e.preventDefault(); first.focus(); }
        }
      }
    });

    this.on(bar, 'focusout', function (e) {
      if (!self.openItem) return;
      if (!e.relatedTarget) {
        setTimeout(function () { if (self.openItem && !bar.contains(document.activeElement)) self.closeAll(); }, 0);
        return;
      }
      if (!bar.contains(e.relatedTarget) || !self.openItem.contains(e.relatedTarget)) self.closeAll();
    });
  };

  MegaNav.prototype.isHoverable = function (e) {
    if (this.mode !== 'desktop' || this.settings.openOn !== 'hover') return false;
    var type = e && e.pointerType;
    return !type || type === 'mouse';
  };

  MegaNav.prototype.scheduleOpen = function (item) {
    var self = this;
    clearTimeout(this.timers.close);
    clearTimeout(this.timers.open);
    if (this.openItem === item) return;
    var delay = this.openItem ? Math.min(60, this.settings.hoverOpenDelay) : this.settings.hoverOpenDelay;
    this.timers.open = setTimeout(function () { self.open(item); }, delay);
  };

  MegaNav.prototype.scheduleClose = function () {
    var self = this;
    clearTimeout(this.timers.open);
    clearTimeout(this.timers.close);
    if (this.editorHold) return;
    this.timers.close = setTimeout(function () { self.closeAll(); }, this.settings.hoverCloseDelay);
  };

  MegaNav.prototype.open = function (item, opts) {
    opts = opts || {};
    clearTimeout(this.timers.open);
    clearTimeout(this.timers.close);
    var switching = !!(this.openItem && this.openItem !== item);
    if (switching) this.close(this.openItem);
    var panel = this.panelOf(item);
    if (!panel) return;
    this.hydrate(panel);
    if (this.bar) {
      clearTimeout(this.timers.switching);
      if (switching) {
        this.bar.setAttribute('data-mn-switching', '');
        var bar = this.bar;
        this.timers.switching = setTimeout(function () { bar.removeAttribute('data-mn-switching'); }, 260);
      } else {
        this.bar.removeAttribute('data-mn-switching');
      }
    }
    this.openItem = item;
    item.setAttribute('data-open', 'true');
    this.setExpanded(item, true);
    this.positionPanel(item);
    if (this.settings.showOverlay && this.overlay && !this.overlayDisabled) this.overlay.setAttribute('data-visible', 'true');
    this.root.setAttribute('data-mn-open', 'true');
    if (this.bar) this.bar.setAttribute('data-mn-open', 'true');
    if (opts.focus) { var nodes = panel.querySelectorAll(FOCUSABLE); for (var fi = 0; fi < nodes.length; fi++) { if (isVisible(nodes[fi])) { nodes[fi].focus(); break; } } }
    this.emit('meganav:open', { instance: this, item: item, panel: panel });
  };

  MegaNav.prototype.panelOf = function (item) {
    return item.querySelector(':scope > [data-mn-panel]:not([hidden])');
  };

  MegaNav.prototype.close = function (item) {
    if (!item) return;
    item.removeAttribute('data-open');
    this.setExpanded(item, false);
    var panel = this.panelOf(item);
    if (panel) { panel.style.position = ''; panel.style.top = ''; panel.style.width = ''; if (item.getAttribute('data-mn-width') !== 'anchored') { panel.style.left = ''; panel.style.right = ''; } }
    if (this.openItem === item) this.openItem = null;
    this.emit('meganav:close', { instance: this, item: item, panel: panel });
  };

  MegaNav.prototype.closeAll = function () {
    clearTimeout(this.timers.open);
    clearTimeout(this.timers.close);
    clearTimeout(this.timers.tab);
    this.editorHold = false;
    if (this.openItem) this.close(this.openItem);
    this.openItem = null;
    this.root.removeAttribute('data-mn-open');
    if (this.bar) this.bar.removeAttribute('data-mn-open');
    if (this.overlay) this.overlay.removeAttribute('data-visible');
  };

  MegaNav.prototype.isRtl = function () { return (document.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl'; };

  MegaNav.prototype.setExpanded = function (item, state) {
    var tg = item.querySelector(':scope > [data-mn-toggle]');
    if (tg) tg.setAttribute('aria-expanded', state ? 'true' : 'false');
  };

  MegaNav.prototype.positionPanel = function (item) {
    var panel = this.panelOf(item);
    if (!panel || !this.bar) return;
    var full = item.getAttribute('data-mn-width') !== 'anchored';
    if (full) {
      if (this.mounted) {
        var r = this.bar.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) { this.closeAll(); return; }
        if (this.fixedContainer) {
          // The panel is absolutely positioned against .mn__bar (position: relative), so offsets are bar-relative
          var c = this.fixedContainer.getBoundingClientRect();
          panel.style.position = 'absolute';
          panel.style.top = Math.round(r.height) + 'px';
          panel.style.left = Math.round(c.left - r.left) + 'px';
          panel.style.right = 'auto';
          panel.style.width = Math.round(c.width) + 'px';
        } else {
          panel.style.position = 'fixed';
          panel.style.top = Math.round(r.bottom) + 'px';
          panel.style.left = '0';
          panel.style.right = '0';
          panel.style.width = '';
        }
        // Never taller than what is left of the viewport: the panel scrolls inside itself instead
        var room = Math.max(160, window.innerHeight - r.bottom - 8);
        panel.style.setProperty('--mn-panel-max', room + 'px');
        panel.classList.add('mn__panel--capped');
      }
      return;
    }
    // Anchored: the panel's containing block is the bar, so place it under its item by hand
    var host = panel.offsetParent || this.bar;
    var hr = host.getBoundingClientRect();
    var ir = item.getBoundingClientRect();
    var rtl = this.isRtl();
    panel.style.left = Math.round(ir.left - hr.left) + 'px';
    panel.style.right = 'auto';
    var rect = panel.getBoundingClientRect();
    var vw = document.documentElement.clientWidth;
    if (rtl || rect.right > vw - 8) { panel.style.left = 'auto'; panel.style.right = Math.round(hr.right - ir.right) + 'px'; rect = panel.getBoundingClientRect(); }
    if (rect.left < 8) { panel.style.left = Math.round(8 - hr.left) + 'px'; panel.style.right = 'auto'; }
  };

  /* Flags the bar when its items overflow so the CSS can show a fade and a thin scrollbar */
  MegaNav.prototype.markOverflow = function () {
    if (!this.bar) return;
    var list = this.bar.querySelector('[data-mn-list]');
    if (!list) return;
    // Measure with flex-start: justify-content:center clips the start of an overflowing
    // row and some engines then report scrollWidth === clientWidth, so the first items
    // stay cut off and cannot be scrolled to.
    list.style.justifyContent = 'flex-start';
    var overflow = list.scrollWidth > list.clientWidth + 1;
    list.style.justifyContent = '';
    this.bar.classList.toggle('mn__bar--scrollable', overflow);
    if (overflow) list.scrollLeft = this.isRtl() ? list.scrollWidth : 0;
  };

  MegaNav.prototype.activateTab = function (panel, tab) {
    if (!panel || !tab) return;
    var id = tab.getAttribute('data-mn-tab');
    slice(panel.querySelectorAll('[data-mn-tab]')).forEach(function (t) {
      var on = t === tab;
      t.classList.toggle('is-active', on);
    });
    slice(panel.querySelectorAll('[data-mn-tabpanel]')).forEach(function (tp) {
      var on = tp.getAttribute('data-mn-tabpanel') === id;
      tp.classList.toggle('is-active', on);
      tp.hidden = !on;
    });
  };

  /* -------------------------------------------------------------- drawer */
  MegaNav.prototype.bindDrawer = function () {
    var self = this;
    if (!this.drawer) return;

    var openers = slice(this.root.querySelectorAll('[data-mn-drawer-open]'));
    if (this.bar && !this.root.contains(this.bar)) openers = openers.concat(slice(this.bar.querySelectorAll('[data-mn-drawer-open]')));
    this.openers = openers;
    openers.forEach(function (btn) {
      self.on(btn, 'click', function (e) { e.preventDefault(); self.toggleDrawer(btn); });
    });

    if (this.settings.mobileTriggerSelector) {
      var sel = this.settings.mobileTriggerSelector;
      this.wireTriggers();
      /* On window, capturing: ahead of the theme's own handler wherever it listens (the button, or a
         delegated listener on document), so the theme's drawer never opens and its button never flips */
      this.on(window, 'click', function (e) {
        if (self.mode !== 'mobile' || !e.target || !e.target.closest) return;
        var trigger = null;
        try { trigger = e.target.closest(sel); } catch (err) { trigger = null; }
        if (!trigger) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        self.toggleDrawer(trigger);
      }, true);
    }

    this.on(this.drawer, 'click', function (e) {
      if (!e.target || !e.target.closest) return;
      var close = e.target.closest('[data-mn-drawer-close]');
      if (close) { self.closeDrawer(); return; }
      var drill = e.target.closest('[data-mn-drill]');
      if (drill) { e.preventDefault(); self.drillTo(drill.getAttribute('data-mn-drill'), drill); return; }
      var back = e.target.closest('[data-mn-back]');
      if (back) { e.preventDefault(); self.drillBack(); return; }
      var acc = e.target.closest('.mn__acc-head');
      if (acc) { e.preventDefault(); self.toggleAccordion(acc); }
    });

    this.on(this.drawer, 'keydown', function (e) {
      if (e.key === 'Tab') self.trapFocus(e);
      if (e.key === (self.isRtl() ? 'ArrowRight' : 'ArrowLeft') && self.drawerStack.length > 1 && !e.target.closest('input, textarea')) { e.preventDefault(); self.drillBack(); }
    });
  };

  MegaNav.prototype.toggleAccordion = function (head, force) {
    var acc = head.closest('[data-mn-accordion]');
    var body = acc && acc.querySelector('.mn__acc-body');
    if (!body) return;
    var open = typeof force === 'boolean' ? force : head.getAttribute('aria-expanded') !== 'true';
    // One at a time: opening a group closes the one already open on the same screen
    if (open && this.settings.accordionSingle !== false) {
      var screen = acc.closest('[data-mn-drawer-body]') || acc.parentNode;
      var self = this;
      if (screen) {
        slice(screen.querySelectorAll('[data-mn-accordion] > .mn__acc-heading > .mn__acc-head[aria-expanded="true"]')).forEach(function (other) {
          if (other !== head) self.toggleAccordion(other, false);
        });
      }
    }
    head.setAttribute('aria-expanded', open ? 'true' : 'false');
    body.hidden = !open;
  };

  MegaNav.prototype.toggleDrawer = function (trigger) {
    if (this.drawerOpen) this.closeDrawer(); else this.openDrawer(trigger);
  };

  MegaNav.prototype.openDrawer = function (trigger) {
    if (!this.drawer || this.drawerOpen) return;
    clearTimeout(this.timers.drawer);
    this.closeAll();
    this.drawerOpen = true;
    this.drawerTrigger = trigger || document.activeElement;
    this.resetDrawer();
    this.hydrate(this.drawerPanel('root'));
    this.preloadTops();
    this.drawer.setAttribute('data-open', 'true');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.lockScroll();
    if (this.drawerOverlay) this.drawerOverlay.setAttribute('data-visible', 'true');
    this.setOpenersExpanded(true, trigger);
    this.setPageInert(true);
    var self = this;
    requestAnimationFrame(function () {
      try { self.drawer.focus({ preventScroll: true }); } catch (e) { self.drawer.focus(); }
    });
    this.emit('meganav:drawer:open');
  };

  MegaNav.prototype.closeDrawer = function () {
    if (!this.drawer || !this.drawerOpen) return;
    this.drawerOpen = false;
    this.drawer.removeAttribute('data-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.unlockScroll();
    if (this.drawerOverlay) this.drawerOverlay.removeAttribute('data-visible');
    this.setOpenersExpanded(false);
    this.setPageInert(false);
    var trigger = this.drawerTrigger;
    if (trigger && typeof trigger.focus === 'function' && document.contains(trigger)) {
      try { trigger.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    }
    var self = this;
    this.timers.drawer = setTimeout(function () { self.resetDrawer(); }, 350);
    this.emit('meganav:drawer:close');
  };

  MegaNav.prototype.lockScroll = function () {
    if (this.scrollLocked) return;
    this.scrollLocked = true;
    this.savedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var gutter = window.innerWidth - document.documentElement.clientWidth;
    var body = document.body;
    body.style.setProperty('--mn-scroll-top', -this.savedScrollY + 'px');
    if (gutter > 0) body.style.paddingRight = gutter + 'px';
    document.documentElement.classList.add('mn-scroll-lock');
  };

  MegaNav.prototype.unlockScroll = function () {
    if (!this.scrollLocked) return;
    this.scrollLocked = false;
    document.documentElement.classList.remove('mn-scroll-lock');
    document.body.style.paddingRight = '';
    document.body.style.removeProperty('--mn-scroll-top');
    var de = document.documentElement, prev = de.style.scrollBehavior;
    de.style.scrollBehavior = 'auto';
    window.scrollTo(0, this.savedScrollY || 0);
    de.style.scrollBehavior = prev;
  };

  /* Everything outside the drawer is inert (or aria-hidden where inert is unsupported) while it is open */
  MegaNav.prototype.setPageInert = function (on) {
    var self = this;
    var keep = [this.drawer, this.drawerOverlay];
    var supportsInert = 'inert' in HTMLElement.prototype;
    if (on) {
      this.inerted = [];
      slice(document.body.children).forEach(function (el) {
        if (keep.indexOf(el) > -1 || el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'LINK') return;
        if (self.drawer && el.contains(self.drawer)) return;
        if (supportsInert) { if (el.inert) return; el.inert = true; }
        else { if (el.getAttribute('aria-hidden') === 'true') return; el.setAttribute('aria-hidden', 'true'); }
        self.inerted.push(el);
      });
    } else {
      (this.inerted || []).forEach(function (el) { if (supportsInert) el.inert = false; else el.removeAttribute('aria-hidden'); });
      this.inerted = [];
    }
  };

  /* Mirror the drawer on every opener: ours, the theme's (queried live), and the one just tapped */
  MegaNav.prototype.setOpenersExpanded = function (state, trigger) {
    var self = this, seen = [];
    var list = (this.openers || []).concat(this.themeTriggers());
    if (trigger && trigger.setAttribute) list.push(trigger);
    list.forEach(function (el) {
      if (!el || seen.indexOf(el) !== -1) return;
      seen.push(el);
      if (state) self.rememberTriggerLook(el); else self.restoreTriggerLook(el);
      el.setAttribute('aria-expanded', state ? 'true' : 'false');
    });
  };

  MegaNav.prototype.drawerPanel = function (id) {
    return this.drawer.querySelector('[data-mn-drawer-panel="' + esc(id) + '"]:not([hidden])');
  };

  MegaNav.prototype.activeDrawerPanel = function () {
    return this.drawerPanel(this.drawerStack[this.drawerStack.length - 1]);
  };

  MegaNav.prototype.setDrawerTitle = function (panel) {
    var el = this.drawer.querySelector('[data-mn-drawer-title]');
    if (!el) return;
    var title = panel && panel.getAttribute('data-mn-title');
    el.textContent = title || el.getAttribute('data-mn-root-title') || '';
    this.drawer.setAttribute('data-mn-depth', String(this.drawerStack.length - 1));
  };

  MegaNav.prototype.drillTo = function (id, trigger) {
    var panel = this.drawerPanel(id);
    if (!panel || panel.classList.contains('is-active')) return;
    this.hydrate(panel);
    this.prefetchAhead(id);
    var current = this.activeDrawerPanel();
    if (current) { current.classList.remove('is-active'); current.classList.add('is-behind'); current.setAttribute('aria-hidden', 'true'); }
    panel.classList.add('is-active');
    panel.classList.remove('is-behind');
    panel.setAttribute('aria-hidden', 'false');
    panel.scrollTop = 0;
    this.drawerStack.push(id);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    this.setDrawerTitle(panel);
    var focusTarget = this.drawer.querySelector('[data-mn-drawer-title]') || this.drawer.querySelector('.mn__drawer-head [data-mn-back]') || panel.querySelector(FOCUSABLE);
    if (focusTarget) { try { focusTarget.focus({ preventScroll: true }); } catch (e) { focusTarget.focus(); } }
    this.emit('meganav:drawer:panel', { instance: this, panel: panel });
  };

  MegaNav.prototype.drillBack = function () {
    if (this.drawerStack.length <= 1) return;
    var leavingId = this.drawerStack.pop();
    var leaving = this.drawerPanel(leavingId);
    var target = this.activeDrawerPanel();
    if (leaving) { leaving.classList.remove('is-active'); leaving.setAttribute('aria-hidden', 'true'); }
    if (target) {
      target.classList.remove('is-behind');
      target.classList.add('is-active');
      target.setAttribute('aria-hidden', 'false');
      var btn = target.querySelector('[data-mn-drill="' + esc(leavingId) + '"]');
      if (btn) { btn.setAttribute('aria-expanded', 'false'); try { btn.focus({ preventScroll: true }); } catch (e) { btn.focus(); } }
    }
    this.setDrawerTitle(this.drawerStack.length > 1 ? target : null);
  };

  /* Back to the main screen, and every screen as new: scrolled to the top, each swipe row at its first card, */
  /* each group closed. Runs out of sight, just after the menu closes and again as it opens.                 */
  MegaNav.prototype.resetDrawer = function () {
    var self = this;
    slice(this.drawer.querySelectorAll('[data-mn-drawer-panel]')).forEach(function (p) {
      var isRoot = p.getAttribute('data-mn-drawer-panel') === 'root';
      p.classList.toggle('is-active', isRoot);
      p.classList.remove('is-behind');
      p.setAttribute('aria-hidden', isRoot ? 'false' : 'true');
      p.scrollTop = 0;
    });
    slice(this.drawer.querySelectorAll('.mn__strip--tiles, .mn__strip .mn__column-list')).forEach(function (row) { row.scrollLeft = 0; });
    slice(this.drawer.querySelectorAll('.mn__acc-head[aria-expanded="true"]')).forEach(function (head) { self.toggleAccordion(head, false); });
    slice(this.drawer.querySelectorAll('[data-mn-drill]')).forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    this.drawerStack = ['root'];
    this.setDrawerTitle(null);
  };

  MegaNav.prototype.trapFocus = function (e) {
    var head = this.drawer.querySelector('.mn__drawer-head');
    var panel = this.activeDrawerPanel();
    var nodes = [];
    if (head) nodes = nodes.concat(slice(head.querySelectorAll(FOCUSABLE)).filter(isVisible));
    if (panel) nodes = nodes.concat(slice(panel.querySelectorAll(FOCUSABLE)).filter(function (n) { return !n.closest('[hidden]') && isVisible(n); }));
    if (!nodes.length) return;
    var first = nodes[0], last = nodes[nodes.length - 1], active = document.activeElement;
    if (e.shiftKey && (active === first || active === this.drawer)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (active === last || active === this.drawer)) { e.preventDefault(); first.focus(); }
    else if (nodes.indexOf(active) === -1) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
  };

  /* -------------------------------------------------------------- editor */
  MegaNav.prototype.bindEditor = function () {
    var self = this;
    var designMode = this.settings.designMode || (window.Shopify && window.Shopify.designMode);

    // Item sections can load/unload at any time in the editor
    this.on(document, 'shopify:section:load', function (e) {
      var el = e.target && e.target.querySelector ? e.target.querySelector('[data-mn-item-section]') : null;
      if (!el) el = document.querySelector('[data-mn-item-section][data-section-id="' + esc(e.detail && e.detail.sectionId) + '"]');
      if (el) { self.attachSection(el); self.hoistSubpanels(); self.revealSection(el.getAttribute('data-section-id')); }
    });
    this.on(document, 'shopify:section:unload', function (e) {
      var sid = e.detail && e.detail.sectionId;
      if (sid === self.settings.id) { self.destroy(); return; }
      if (!sid || !self.sections[sid]) return;
      var key = self.sections[sid].key;
      self.detachSection(sid);
      // A duplicate section was removed: hand the item back to the section it displaced. The editor may
      // remove the section's DOM before or after this event, so look once it has settled.
      setTimeout(function () {
        var others = slice(document.querySelectorAll('[data-mn-item-section][data-mn-for="' + esc(key) + '"]:not([data-mn-attached])')).filter(function (el) { return el.getAttribute('data-section-id') !== sid; });
        if (others.length && !self.bar) return;
        if (others.length && !document.querySelector('[data-mn-item-section][data-mn-for="' + esc(key) + '"][data-mn-attached="true"]')) { self.attachSection(others[0]); self.hoistSubpanels(); others.shift(); }
        if (self.duplicateKeys && self.duplicateKeys[key] && !others.length) {
          delete self.duplicateKeys[key];
          if (self.settings.designMode) self.noteSections();
        }
      }, 0);
    });

    if (!designMode) return;

    this.on(document, 'shopify:block:select', function (e) {
      var id = e.detail && e.detail.blockId;
      if (!id) return;
      var el = self.findBlockElement(id);
      if (!el) return;
      self.revealElement(el);
    });
    this.on(document, 'shopify:block:deselect', function () { self.editorHold = false; self.closeAll(); });
    this.on(document, 'shopify:section:select', function (e) {
      var sid = e.detail && e.detail.sectionId;
      if (sid === self.settings.id) {
        var firstItem = self.bar && self.bar.querySelector('[data-mn-item][data-mn-layout]');
        if (self.mode === 'desktop' && !self.openItem && firstItem) { self.editorHold = true; self.open(firstItem); }
        return;
      }
      self.revealSection(sid);
    });
    this.on(document, 'shopify:section:deselect', function () { self.editorHold = false; self.closeAll(); self.closeDrawer(); });
  };

  MegaNav.prototype.revealSection = function (sid) {
    var info = this.sections[sid];
    if (!info) return;
    if (this.mode === 'desktop') {
      var item = this.bar.querySelector('[data-mn-item][data-mn-key="' + esc(info.key) + '"]');
      if (item && item.hasAttribute('data-mn-layout')) { this.editorHold = true; this.open(item); }
    } else if (this.drawer) {
      if (this.drawerOpen) this.resetDrawer(); else this.openDrawer();
      this.drillTo(info.index, this.drawer.querySelector('[data-mn-drill="' + esc(info.index) + '"]'));
    }
  };

  MegaNav.prototype.revealElement = function (el) {
    if (this.mode === 'desktop') {
      var item = el.closest('[data-mn-item]');
      if (!item || !item.hasAttribute('data-mn-layout')) return;
      this.editorHold = true;
      this.open(item);
      var tp = el.closest('[data-mn-tabpanel]');
      if (tp) {
        var panel = this.panelOf(item);
        var tab = panel && panel.querySelector('[data-mn-tab="' + esc(tp.getAttribute('data-mn-tabpanel')) + '"]');
        if (tab) this.activateTab(panel, tab);
      }
    } else {
      var dp = el.closest('[data-mn-drawer-panel]');
      if (!dp || dp.hidden) return;
      if (this.drawerOpen) this.resetDrawer(); else this.openDrawer();
      var chain = [], cursor = dp, self = this;
      while (cursor && cursor.getAttribute('data-mn-drawer-panel') !== 'root') {
        chain.unshift(cursor.getAttribute('data-mn-drawer-panel'));
        var parentId = cursor.getAttribute('data-mn-parent');
        cursor = parentId ? this.drawerPanel(parentId) : null;
      }
      chain.forEach(function (pid) { self.drillTo(pid, self.drawer.querySelector('[data-mn-drill="' + esc(pid) + '"]')); });
      var acc = el.closest('[data-mn-accordion]');
      if (acc) this.toggleAccordion(acc.querySelector('.mn__acc-head'), true);
      else { var innerAcc = el.querySelector && el.querySelector('.mn__acc-head'); if (innerAcc) this.toggleAccordion(innerAcc, true); }
    }
  };

  MegaNav.prototype.findBlockElement = function (blockId) {
    var scope = this.mode === 'desktop' ? this.bar : this.drawer;
    var el = scope && scope.querySelector('[data-mn-block="' + esc(blockId) + '"]:not([hidden])');
    if (!el) el = document.querySelector('[data-mn-block="' + esc(blockId) + '"]');
    return el;
  };

  /* ------------------------------------------------------------- destroy */
  MegaNav.prototype.showThemeElements = function () {
    if (this.hideStyle && this.hideStyle.parentNode) this.hideStyle.parentNode.removeChild(this.hideStyle);
    this.hideStyle = null;
    slice(document.querySelectorAll('[data-mn-hidden]')).forEach(function (el) {
      el.style.removeProperty('display');
      el.removeAttribute('data-mn-hidden');
    });
  };

  MegaNav.prototype.destroy = function () {
    this.closeAll();
    this.closeDrawer();
    clearTimeout(this.timers.drawer);
    this.listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
    this.listeners = [];
    if (this.mql) { if (this.mql.removeEventListener) this.mql.removeEventListener('change', this.mqlHandler); else this.mql.removeListener(this.mqlHandler); }
    if (this.mo) this.mo.disconnect();
    this.setPageInert(false);
    this.showThemeElements();
    document.documentElement.classList.remove('mn-fallback');
    var self = this;
    Object.keys(this.sections).forEach(function (sid) { self.detachSection(sid); });
    Object.keys(this.timers).forEach(function (k) { clearTimeout(self.timers[k]); });
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.bar && this.bar.hasAttribute('data-mn-aria-label')) { this.bar.setAttribute('aria-label', this.bar.getAttribute('data-mn-aria-label')); this.bar.removeAttribute('data-mn-aria-label'); this.bar.removeAttribute('role'); }
    if (this.mounted && this.bar && this.bar.parentNode) this.bar.parentNode.removeChild(this.bar);
    if (this.mountStash && this.mountTarget) this.mountTarget.appendChild(this.mountStash);
    if (this.drawer && this.drawer.parentNode === document.body) document.body.removeChild(this.drawer);
    if (this.drawerOverlay && this.drawerOverlay.parentNode === document.body) document.body.removeChild(this.drawerOverlay);
    if (this.overlay && this.overlay.parentNode && this.overlay.parentNode !== this.root) this.overlay.parentNode.removeChild(this.overlay);
    slice(document.querySelectorAll('[data-mn-hidden]')).forEach(function (el) { el.style.removeProperty('display'); el.removeAttribute('data-mn-hidden'); });
    if (this.editorNote && this.editorNote.parentNode && !this.editorNote.querySelector('strong')) this.editorNote.parentNode.removeChild(this.editorNote);
    document.documentElement.classList.remove('mn-scroll-lock');
    var i = MegaNav.instances.indexOf(this);
    if (i > -1) MegaNav.instances.splice(i, 1);
  };

  function initAll() {
    slice(document.querySelectorAll('[data-mega-nav]:not([data-mn-ready])')).forEach(function (root) { new MegaNav(root); });
    if (MegaNav.instances[0]) MegaNav.instances[0].attachAll();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll); else initAll();
  document.addEventListener('shopify:section:load', function () { initAll(); });

  window.MegaNav = MegaNav;
})();
